import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import { BRAND_OPTIONS } from '../constants';
import { OfferConfig } from '../types';
import { deriveIsActive, getDateTimeValue } from '../utils';

type OfferMetadataFormProps = {
    offer: OfferConfig;
    canEdit: boolean;
    onFieldChange: (field: keyof OfferConfig, value: string) => void;
};

export default function OfferMetadataForm({ offer, canEdit, onFieldChange }: OfferMetadataFormProps) {
    const isActive = offer.is_active ?? deriveIsActive(
        offer.valid_from,
        offer.valid_till,
        offer.valid_from_time,
        offer.valid_till_time
    );
    const disabled = !canEdit;
    const validFromDateTime = getDateTimeValue(offer.valid_from, offer.valid_from_time);
    const validTillDateTime = getDateTimeValue(offer.valid_till, offer.valid_till_time);

    return (
        <div className="oz-offer-language-config-metadata-form">
            <div className="oz-offer-language-config-metadata-row">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography className="oz-offer-language-config-field-label">Offer Name</Typography>
                    <TextField
                        value={offer.offer_name}
                        onChange={(e) => onFieldChange('offer_name', e.target.value)}
                        disabled={disabled}
                        fullWidth
                        placeholder="Enter offer name"
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography className="oz-offer-language-config-field-label">Brand</Typography>
                    <Autocomplete
                        options={[...BRAND_OPTIONS]}
                        value={offer.brand}
                        size="small"
                        disabled={disabled}
                        onChange={(_, value) => {
                            if (value) onFieldChange('brand', value);
                        }}
                        disableClearable
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select brand"
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                        )}
                    />
                </Box>
            </div>
            <div className="oz-offer-language-config-metadata-row">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography className="oz-offer-language-config-field-label">Valid From</Typography>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            inputFormat="DD-MMM-YYYY HH:mm:ss"
                            value={validFromDateTime ? moment(validFromDateTime) : null}
                            disabled={disabled}
                            onChange={(date) => {
                                if (date && moment.isMoment(date)) {
                                    onFieldChange('valid_from', date.format('YYYY-MM-DD'));
                                    onFieldChange('valid_from_time', date.format('HH:mm:ss'));
                                } else {
                                    onFieldChange('valid_from', '');
                                    onFieldChange('valid_from_time', '');
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    size="small"
                                    placeholder="Select date and time"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            fontSize: '14px',
                                        },
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography className="oz-offer-language-config-field-label">Valid Till</Typography>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            inputFormat="DD-MMM-YYYY HH:mm:ss"
                            value={validTillDateTime ? moment(validTillDateTime) : null}
                            disabled={disabled}
                            minDateTime={validFromDateTime ? moment(validFromDateTime) : undefined}
                            onChange={(date) => {
                                if (date && moment.isMoment(date)) {
                                    onFieldChange('valid_till', date.format('YYYY-MM-DD'));
                                    onFieldChange('valid_till_time', date.format('HH:mm:ss'));
                                } else {
                                    onFieldChange('valid_till', '');
                                    onFieldChange('valid_till_time', '');
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    size="small"
                                    placeholder="Select date and time"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            fontSize: '14px',
                                        },
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'end' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                        <Chip
                            size="small"
                            label={'Status: ' + (isActive ? 'Active' : 'Inactive')}
                            color={isActive ? 'success' : 'default'}
                            variant={isActive ? 'filled' : 'outlined'}
                        />
                    </Box>
                </Box>
            </div>
        </div>
    );
}
